import { useEffect, useRef } from "react";
import { ArrowRight, ShieldCheck, Sparkles, Apple } from "lucide-react";
import { BRAND } from "@/lib/brand";
import AnimatedCounter from "./AnimatedCounter";

export default function Hero({ onCta }) {
  const containerRef = useRef(null);
  const bgRef = useRef(null);
  const overlayRef = useRef(null);

  // Parallax du fond + fondu de l'overlay, sans framer-motion (rAF + scroll passif)
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const y = window.scrollY;
      if (y > 1200) return;
      if (bgRef.current) bgRef.current.style.transform = `translate3d(0, ${Math.min(200, y * 0.2)}px, 0)`;
      if (overlayRef.current) overlayRef.current.style.opacity = String(Math.max(0.6, 1 - (y / 800) * 0.4));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        }),
      { rootMargin: "-80px 0px" }
    );
    containerRef.current?.querySelectorAll(".df-inview").forEach((el) => io.observe(el));
    return () => {
      window.removeEventListener("scroll", onScroll);
      io.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      ref={containerRef}
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden bg-[#0A0A0A] text-white grain"
    >
      {/* Parallax background */}
      <div ref={bgRef} className="absolute inset-0 will-change-transform">
        <img
          src="/hero/hero-1600.webp"
          srcSet="/hero/hero-640.webp 640w, /hero/hero-1024.webp 1024w, /hero/hero-1600.webp 1600w"
          sizes="(max-width: 767px) 60vw, 100vw"
          alt=""
          width="1600"
          height="900"
          fetchPriority="high"
          decoding="async"
          className="w-full h-[120%] object-cover opacity-60"
        />
        <div ref={overlayRef} className="absolute inset-0 bg-gradient-to-b from-black/40 via-[#0A0A0A]/85 to-[#0A0A0A]" />
      </div>

      {/* Speed lines */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(14)].map((_, i) => (
          <span
            key={i}
            className="speed-line"
            style={{
              top: `${10 + i * 6}%`,
              width: `${30 + (i % 4) * 18}%`,
              animationDelay: `${(i * 0.35).toFixed(2)}s`,
              animationDuration: `${(2.6 + (i % 5) * 0.4).toFixed(2)}s`,
              left: `-${20 + (i % 3) * 10}%`,
            }}
          />
        ))}
      </div>

      {/* Blue spotlight */}
      <div className="absolute inset-0 spotlight pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-40 pb-24 lg:pt-48 lg:pb-40">
        <div className="max-w-4xl">
          <div
            className="df-rise inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 bg-white/5 backdrop-blur-sm text-xs tracking-[0.2em] uppercase font-bold text-white/80 mb-10"
            style={{ animationDuration: "0.7s" }}
            data-testid="hero-badge"
          >
            <Sparkles size={14} className="text-[#38BDF8]" />
            <span>App de gestion pour les VTC et Taxis</span>
          </div>

          <h1
            className="font-display text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tighter font-light text-white"
            data-testid="hero-title"
          >
            {[
              { text: "Une seule app.", className: "" },
              { text: "30 secondes par course.", className: "font-medium" },
              { text: "Pour moins d'1 € HT par jour.", className: "text-white/60" },
            ].map((w, i) => (
              <span key={i} className="df-rise block overflow-hidden" style={{ animationDelay: `${0.15 + i * 0.18}s` }}>
                <span className={w.className}>{w.text}</span>
              </span>
            ))}
          </h1>

          <p
            className="df-rise mt-8 max-w-2xl text-lg lg:text-xl text-white/70 leading-relaxed font-light"
            style={{ animationDuration: "0.8s", animationDelay: "0.85s" }}
            data-testid="hero-subtitle"
          >
            VTC indépendants, taxis conventionnés CPAM, sociétés de transport : du premier appel client jusqu'au bilan comptable, DriveFlow centralise toute votre activité. Réservations, agenda, facturation Factur-X, exports automatiques pour le comptable, commissions entre confrères. Sans abonnement avec les jetons, ou illimité pour moins d'1 € HT par jour.
          </p>

          <div className="df-rise mt-12" style={{ animationDuration: "0.8s", animationDelay: "1.05s" }}>
            <span
              data-testid="hero-promo-badge"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.18em] font-bold border border-[#38BDF8]/40 bg-[#38BDF8]/12 text-[#38BDF8] mb-3"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#38BDF8] opacity-70" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#38BDF8]" />
              </span>
              Offre limitée — 1 mois gratuit
            </span>
            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center" data-testid="hero-cta-row">
              <MagneticButton
                onClick={onCta}
                data-testid="hero-cta-primary"
                className="group bg-white text-[#0F1B3D] hover:bg-[#F1F5F9] px-8 py-5 rounded-full font-semibold transition-all duration-300 shadow-[0_0_40px_rgba(255,255,255,0.15)] flex items-center justify-center gap-2"
              >
                Démarrer mon essai gratuit
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </MagneticButton>
              <a
                href="#fonctionnalites"
                data-testid="hero-cta-secondary"
                className="bg-transparent text-white border border-white/20 hover:bg-white/5 hover:border-white/40 px-8 py-5 rounded-full font-medium transition-all duration-300 flex items-center justify-center"
              >
                Découvrir les fonctionnalités
              </a>
            </div>
          </div>

          <div className="df-fade mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/60" style={{ animationDelay: "1.3s" }} data-testid="hero-trust">
            {[
              "30 jours offerts",
              "Sans carte bancaire",
              "Sans engagement",
              BRAND.origin,
            ].map((t) => (
              <span key={t} className="flex items-center gap-2">
                <ShieldCheck size={16} className="text-[#38BDF8]" /> {t}
              </span>
            ))}
          </div>

          {/* Badges stores (style officiel : fond noir, plus grands) */}
          <div className="df-fade mt-8 flex flex-wrap gap-3" style={{ animationDelay: "1.45s" }} data-testid="hero-store-badges">
            <a
              href="https://apps.apple.com/fr/app/driveflow-gestion-taxi-vtc/id6791095016"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-app-store-badge"
              aria-label="Télécharger DriveFlow sur l'App Store"
              className="flex items-center gap-3 h-14 pl-4 pr-5 rounded-xl bg-black text-white border border-white/25 hover:border-white/60 hover:bg-[#111] transition-all duration-300 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.8)]"
            >
              <Apple size={28} className="shrink-0 -mt-0.5" fill="#fff" strokeWidth={0} />
              <span className="text-left leading-none">
                <span className="block text-[10px] tracking-wide text-white/85 mb-1">Télécharger dans</span>
                <span className="block text-[19px] font-semibold tracking-tight">App Store</span>
              </span>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=fr.mydriveflow.driveflow"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-play-store-badge"
              aria-label="Télécharger DriveFlow sur Google Play"
              className="flex items-center gap-3 h-14 pl-4 pr-5 rounded-xl bg-black text-white border border-white/25 hover:border-white/60 hover:bg-[#111] transition-all duration-300 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.8)]"
            >
              <GooglePlayLogo className="w-7 h-7 shrink-0" />
              <span className="text-left leading-none">
                <span className="block text-[10px] uppercase tracking-wide text-white/85 mb-1">Disponible sur</span>
                <span className="block text-[19px] font-semibold tracking-tight">Google Play</span>
              </span>
            </a>
          </div>
        </div>

        {/* Stats strip with animated counters */}
        <div
          className="df-inview mt-24 lg:mt-32 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-3xl overflow-hidden border border-white/10"
          data-testid="hero-stats"
        >
          <StatCell
            label="Courses gérées par le fondateur"
            value={10000}
            format={(n) => `${n.toLocaleString("fr-FR")}+`}
          />
          <StatCell
            label="Pour créer une course"
            value={30}
            format={(n) => `${n} sec`}
          />
          <StatCell
            label="Avis Google / mois en moyenne"
            value={12}
            format={(n) => `+${n}`}
          />
          <StatCell
            label="Made in France 🇫🇷"
            value={100}
            format={(n) => `${n} %`}
          />
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="df-fade absolute bottom-7 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 pointer-events-none"
        style={{ animationDelay: "1.8s", animationDuration: "0.8s" }}
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] tracking-[0.35em] uppercase text-white/40 font-bold">Scroll</span>
        <span className="relative w-px h-12 bg-white/15 overflow-hidden">
          <span className="df-scroll-dot absolute inset-x-0 h-1/2 bg-gradient-to-b from-transparent via-[#38BDF8] to-[#38BDF8]" />
        </span>
      </div>
    </section>
  );
}

function StatCell({ value, label, format }) {
  return (
    <div className="group bg-[#0A0A0A] p-8 transition-all duration-500 hover:bg-[#0F1B3D]/30">
      <AnimatedCounter
        value={value}
        format={format}
        duration={2}
        className="font-display text-3xl lg:text-4xl font-medium text-white block"
      />
      <div className="mt-2 text-sm text-white/55 leading-relaxed group-hover:text-white/75 transition-colors">
        {label}
      </div>
    </div>
  );
}

// Subtle magnetic button — slight translation toward cursor on hover
function MagneticButton({ children, className, onClick, ...rest }) {
  const ref = useRef(null);
  const handleMove = (e) => {
    const node = ref.current;
    if (!node) return;
    const r = node.getBoundingClientRect();
    const x = e.clientX - r.left - r.width / 2;
    const y = e.clientY - r.top - r.height / 2;
    node.style.transform = `translate(${x * 0.15}px, ${y * 0.2}px)`;
  };
  const handleLeave = () => {
    if (ref.current) ref.current.style.transform = "translate(0,0)";
  };
  return (
    <button
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onClick={onClick}
      className={className}
      style={{ willChange: "transform", transition: "transform 0.25s ease" }}
      {...rest}
    >
      {children}
    </button>
  );
}

// Logo Google Play officiel (4 couleurs)
function GooglePlayLogo({ className = "" }) {
  return (
    <svg viewBox="0 0 512 512" className={className} aria-hidden="true">
      <path fill="#00D7FE" d="M48 32c-8 5-13 14-13 26v396c0 12 5 21 13 26l229-224L48 32z" />
      <path fill="#FFCE00" d="M354 333l77-44c22-13 22-33 0-46l-77-45-79 58 79 77z" />
      <path fill="#00F076" d="M48 32l229 224 78-77L69 25c-8-4-15-4-21 7z" />
      <path fill="#FF3A44" d="M48 480c6 11 13 11 21 7l286-166-78-77L48 480z" />
    </svg>
  );
}
